import { useMemo, useState } from 'react';
import type { FeedItem } from '@/mocks/gossip';
import { catMeta, feedItems, filters, rankItems } from '@/mocks/gossip';
import ArticleModal from './ArticleModal';

export default function FeedSection() {
  const [active, setActive] = useState<'all' | keyof typeof catMeta>('all');
  const [openItem, setOpenItem] = useState<FeedItem | null>(null);

  const list = useMemo(
    () => (active === 'all' ? feedItems : feedItems.filter((it) => it.cat === active)),
    [active]
  );

  return (
    <section id="feed" className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="mb-2 inline-flex items-center gap-2 text-xs font-semibold tracking-widest text-primary-600">
            <span className="h-px w-6 bg-primary-500" />
            GOSSIP FEED
          </p>
          <h2 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">八卦速递 · 图文合集精选</h2>
          <p className="mt-2 max-w-2xl text-sm text-foreground-600">
            明星八卦合集每日更新，从速递到大瓜深扒、红毯与幕后，点开任意一张卡片即可静心阅读。
          </p>
        </div>
        <span className="rounded-full bg-accent-100 px-4 py-1.5 text-xs font-medium text-accent-800">
          已收录 {feedItems.length}+ 篇精选图文
        </span>
      </div>

      <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
        <div className="min-w-0 flex-1">
          <div className="mb-6 flex flex-wrap gap-2" role="tablist" aria-label="栏目筛选">
            {filters.map((f) => (
              <button
                key={f.key}
                type="button"
                role="tab"
                aria-selected={active === f.key}
                onClick={() => setActive(f.key)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition ${
                  active === f.key
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-secondary-100 text-foreground-700 hover:bg-secondary-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
            {list.map((it) => (
              <button
                key={it.id}
                type="button"
                onClick={() => setOpenItem(it)}
                className="group flex cursor-pointer flex-col overflow-hidden rounded-xl border border-background-200 bg-background-50 text-left transition hover:-translate-y-1 hover:border-primary-200"
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-secondary-100">
                  <img
                    src={it.img}
                    alt={it.alt}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 rounded-full bg-background-50/90 px-3 py-1 text-[11px] font-semibold text-primary-700 backdrop-blur">
                    {catMeta[it.cat]}
                  </span>
                  <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-full bg-foreground-950/55 px-2.5 py-1 text-[11px] font-semibold text-background-50 backdrop-blur">
                    <i className="ri-fire-line text-accent-200" />
                    {it.heat}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-4 md:p-5">
                  <h3 className="line-clamp-2 font-heading text-[15px] font-bold leading-snug text-foreground-950 md:text-base">
                    {it.title}
                  </h3>
                  <p className="mt-2 line-clamp-2 flex-1 text-xs leading-5 text-foreground-600 md:text-[13px]">{it.excerpt}</p>
                  <div className="mt-3 flex items-center justify-between border-t border-background-200 pt-3 text-[11px] text-foreground-500">
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-time-line" />
                      {it.time}
                    </span>
                    <span className="inline-flex items-center gap-1 font-medium text-primary-600 opacity-0 transition group-hover:opacity-100">
                      开始阅读
                      <i className="ri-arrow-right-line" />
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <aside className="w-full shrink-0 lg:sticky lg:top-28 lg:w-[300px] xl:w-[330px]">
          <div className="rounded-xl border border-background-200 bg-background-50 p-5">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-heading text-base font-bold text-foreground-950">瓜田热榜 TOP 8</h3>
              <i className="ri-hotspot-line text-primary-500" />
            </div>
            <ol className="space-y-1">
              {rankItems.map((r, i) => (
                <li
                  key={r.id}
                  className="flex items-center gap-3 rounded-lg px-2 py-2 transition hover:bg-secondary-100"
                >
                  <span
                    className={`flex h-6 w-6 shrink-0 items-center justify-center rounded text-xs font-bold ${
                      i < 3 ? 'bg-primary-500 text-background-50' : 'bg-secondary-200 text-foreground-700'
                    }`}
                  >
                    {i + 1}
                  </span>
                  <p className="min-w-0 flex-1 truncate text-[13px] text-foreground-800">{r.title}</p>
                  <span
                    className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold ${
                      r.hot === '爆' ? 'bg-primary-100 text-primary-700' : 'bg-accent-100 text-accent-800'
                    }`}
                  >
                    {r.hot}
                  </span>
                </li>
              ))}
            </ol>
          </div>
          <a
            href="#changtu"
            className="mt-4 flex items-center gap-3 rounded-xl bg-accent-500 p-4 transition hover:bg-accent-600"
          >
            <i className="ri-image-2-line text-2xl text-background-50" />
            <span className="flex-1">
              <span className="block text-sm font-bold text-background-50">长图合集已上新</span>
              <span className="block text-[11px] text-background-50/85">八卦长图全收录，长按即可保存分享</span>
            </span>
            <i className="ri-arrow-right-line text-background-50" />
          </a>
        </aside>
      </div>
      <ArticleModal item={openItem} onClose={() => setOpenItem(null)} />
    </section>
  );
}